// Knots on the Living Khipu's cords. Each scene lists the knots it wants;
// a knot already tied moves to its new state (wound on, pulled tight,
// loosened), a new one is tied from nothing, and one the scene no longer
// wants unwinds and goes.
import { Cycle, KNOT, S, cycleLength, stateAt } from "../lib/knot";
import { Cord, KnotRun } from "./cords";
import { SceneCords } from "./scenes";

/** Plan a knot's move to `p` turns at tightness `c`, after `delay` ms. */
export const planKnot = (
  kn: KnotRun,
  p: number,
  c: number,
  delay: number,
  instant: boolean,
  now: number,
) => {
  const { st } = kn;
  if (instant) {
    kn.st = S(p, c);
    kn.cyc = null;
    return;
  }
  const cyc: Cycle = [];
  if (p > st.p + 1e-3) {
    // Loosen, wind the new turns on, then pull tight.
    if (st.c > 0.05 && st.p > 0.05)
      cyc.push([280, S(st.p, st.c), S(st.p, 0), "inOut"]);
    cyc.push([200 * (p - st.p) + 140, S(st.p, 0), S(p, 0), "lin"]);
    if (c > 0.02) cyc.push([520, S(p, 0), S(p, c), "cinch"]);
  } else if (p < st.p - 1e-3) {
    // Loosen and unwind.
    if (st.c > 0.05) cyc.push([300, S(st.p, st.c), S(st.p, 0), "inOut"]);
    cyc.push([150 * (st.p - p) + 120, S(st.p, 0), S(p, 0), "lin"]);
    if (c > 0.02 && p > 0) cyc.push([480, S(p, 0), S(p, c), "cinch"]);
  } else if (Math.abs(c - st.c) > 0.01) {
    const tighter = c > st.c;
    cyc.push([
      tighter ? 520 : 360,
      S(st.p, st.c),
      S(p, c),
      tighter ? "cinch" : "inOut",
    ]);
  }
  if (!cyc.length) {
    kn.cyc = null;
    return;
  }
  if (delay) cyc.unshift([delay, S(st.p, st.c), S(st.p, st.c), "lin"]);
  kn.cyc = cyc;
  kn.t0 = now;
};

/** Move every cord's knots to what `scene` wants. */
export const applyKnots = (
  cords: Cord[],
  scene: SceneCords,
  instant: boolean,
  now: number,
) => {
  for (const c of cords) {
    const want = scene[c.i]?.knots ?? [];
    const ids = new Set(want.map((w) => w.id));
    for (const kn of c.knots.values()) {
      if (ids.has(kn.id) || kn.dying) continue;
      kn.dying = true;
      planKnot(kn, 0, 0, 0, instant, now);
    }
    for (const w of want) {
      let kn = c.knots.get(w.id);
      if (!kn) {
        kn = {
          id: w.id,
          st: S(0, 0),
          cyc: null,
          t0: now,
          n: w.n,
          s: w.s,
          k: 0,
          kk: 1,
          spanPx: 0,
          top: false,
          grow: false,
          dying: false,
        };
        c.knots.set(w.id, kn);
      }
      kn.dying = false;
      kn.s = w.s;
      kn.k = w.k || 0;
      kn.top = !!w.top;
      kn.grow = !!w.grow;
      // Never fewer turns than are wound on right now.
      kn.n = Math.max(w.n, Math.ceil(kn.st.p - 1e-6));
      planKnot(kn, w.p ?? w.n, w.c ?? 1, w.delay || 0, instant, now);
    }
  }
};

/** Advance every knot to `now`, and drop the ones fully unwound. */
export const tickKnots = (cords: Cord[], now: number) => {
  for (const c of cords) {
    for (const kn of c.knots.values()) {
      if (kn.cyc) {
        const el = now - kn.t0;
        if (el >= cycleLength(kn.cyc)) {
          kn.st = kn.cyc[kn.cyc.length - 1][2];
          kn.cyc = null;
        } else kn.st = stateAt(el, kn.cyc);
      }
      if (kn.dying && !kn.cyc && kn.st.p <= 0.001) {
        c.knots.delete(kn.id);
        continue;
      }
      kn.kk = kn.k || c.w / KNOT.w;
      kn.spanPx = ((kn.n - 1) * KNOT.pitchTight + KNOT.pitchLoose) * kn.kk;
    }
  }
};
